import { all, fork, put, takeEvery, takeLatest, call } from "redux-saga/effects";
import { SagaIterator } from "@redux-saga/core";
import {
  AgeingBucketsFetchRequest,
  DashboardOperationsSummaryFetchRequest,
  DashboardPortfolioAnalyticsSummaryFetchRequest,
  DashboardReduxTypes,
  DisbursementByFYGraphFetchRequest,
  ProductMixFetchRequest,
  ProgramPerformanceFetchRequest,
  RenewalAndExpiryFetchRequest,
  TxnsByUserFetchRequest
} from "./types";
import {
  fetchAgeingBucketsApi,
  fetchDashboardOperationsSummaryApi,
  fetchDashboardPortfolioAnalyticsSummaryApi,
  fetchDisbursementByFYGraphApi,
  fetchProductMixApi,
  fetchProgramPerformanceApi,
  fetchRenewalAndExpiryApi,
  fetchTxnsByUserApi
} from "./api";

function* fetchDashboardOperationsSummary({ payload }: DashboardOperationsSummaryFetchRequest): SagaIterator {
  try {
    const response = yield call(fetchDashboardOperationsSummaryApi, payload);
    yield put({
      type: DashboardReduxTypes.FETCH_DASHBOARD_OPERATIONS_SUMMARY_SUCCESS,
      payload: { data: response.data.data }
    });
  } catch (error: any) {
    yield put({
      type: DashboardReduxTypes.FETCH_DASHBOARD_OPERATIONS_SUMMARY_ERROR,
      payload: { error: error }
    });
  }
}

function* fetchRenewalAndExpiry({ payload }: RenewalAndExpiryFetchRequest): SagaIterator {
  try {
    const response = yield call(fetchRenewalAndExpiryApi, payload);
    yield put({
      type: DashboardReduxTypes.FETCH_RENEWAL_AND_EXPIRY_SUCCESS,
      payload: { data: response.data.data }
    });
  } catch (error: any) {
    yield put({
      type: DashboardReduxTypes.FETCH_RENEWAL_AND_EXPIRY_ERROR,
      payload: { error: error }
    });
  }
}

function* fetchTxnsByUser({ payload }: TxnsByUserFetchRequest): SagaIterator {
  try {
    const response = yield call(fetchTxnsByUserApi, payload);
    yield put({
      type: DashboardReduxTypes.FETCH_TXNS_BY_USER_SUCCESS,
      payload: { data: response.data.data }
    });
  } catch (error: any) {
    yield put({
      type: DashboardReduxTypes.FETCH_TXNS_BY_USER_ERROR,
      payload: { error: error }
    });
  }
}

function* fetchProgramPerformance({ payload }: ProgramPerformanceFetchRequest): SagaIterator {
  try {
    const response = yield call(fetchProgramPerformanceApi, payload);
    yield put({
      type: DashboardReduxTypes.FETCH_PROGRAM_PERFORMANCE_SUCCESS,
      payload: { data: response.data.data }
    });
  } catch (error: any) {
    yield put({
      type: DashboardReduxTypes.FETCH_PROGRAM_PERFORMANCE_ERROR,
      payload: { error: error }
    });
  }
}

function* fetchProductMix({ payload }: ProductMixFetchRequest): SagaIterator {
  try {
    const response = yield call(fetchProductMixApi, payload);
    yield put({
      type: DashboardReduxTypes.FETCH_PRODUCT_MIX_SUCCESS,
      payload: { data: response.data.data }
    });
  } catch (error: any) {
    yield put({
      type: DashboardReduxTypes.FETCH_PRODUCT_MIX_ERROR,
      payload: { error: error }
    });
  }
}

function* fetchDashboardPortfolioAnalyticsSummary({
  payload
}: DashboardPortfolioAnalyticsSummaryFetchRequest): SagaIterator {
  try {
    const response = yield call(fetchDashboardPortfolioAnalyticsSummaryApi, payload);
    yield put({
      type: DashboardReduxTypes.FETCH_DASHBOARD_PORTFOLIO_ANALYTICS_SUMMARY_SUCCESS,
      payload: { data: response.data.data }
    });
  } catch (error: any) {
    yield put({
      type: DashboardReduxTypes.FETCH_DASHBOARD_PORTFOLIO_ANALYTICS_SUMMARY_ERROR,
      payload: { error: error }
    });
  }
}

function* fetchAgeingBuckets({ payload }: AgeingBucketsFetchRequest): SagaIterator {
  try {
    const response = yield call(fetchAgeingBucketsApi, payload);
    yield put({
      type: DashboardReduxTypes.FETCH_AGEING_BUCKETS_SUCCESS,
      payload: { data: response.data.data }
    });
  } catch (error: any) {
    yield put({
      type: DashboardReduxTypes.FETCH_AGEING_BUCKETS_ERROR,
      payload: { error: error }
    });
  }
}

function* fetchDisbursementByFYGraph({ payload }: DisbursementByFYGraphFetchRequest): SagaIterator {
  try {
    const response = yield call(fetchDisbursementByFYGraphApi, payload);
    yield put({
      type: DashboardReduxTypes.FETCH_DISBURSEMENT_BY_FY_GRAPH_SUCCESS,
      payload: { data: response.data.data }
    });
  } catch (error: any) {
    yield put({
      type: DashboardReduxTypes.FETCH_DISBURSEMENT_BY_FY_GRAPH_ERROR,
      payload: { error: error }
    });
  }
}

export function* watchFetchDashboardOperationsSummary() {
  yield takeEvery(DashboardReduxTypes.FETCH_DASHBOARD_OPERATIONS_SUMMARY, fetchDashboardOperationsSummary);
}

export function* watchFetchRenewalAndExpiry() {
  yield takeLatest(DashboardReduxTypes.FETCH_RENEWAL_AND_EXPIRY, fetchRenewalAndExpiry);
}

export function* watchFetchTxnsByUser() {
  yield takeLatest(DashboardReduxTypes.FETCH_TXNS_BY_USER, fetchTxnsByUser);
}

export function* watchFetchProgramPerformance() {
  yield takeLatest(DashboardReduxTypes.FETCH_PROGRAM_PERFORMANCE, fetchProgramPerformance);
}

export function* watchFetchProductMix() {
  yield takeLatest(DashboardReduxTypes.FETCH_PRODUCT_MIX, fetchProductMix);
}

export function* watchFetchDashboardPortfolioAnalyticsSummary() {
  yield takeEvery(
    DashboardReduxTypes.FETCH_DASHBOARD_PORTFOLIO_ANALYTICS_SUMMARY,
    fetchDashboardPortfolioAnalyticsSummary
  );
}

export function* watchFetchAgeingBuckets() {
  yield takeEvery(DashboardReduxTypes.FETCH_AGEING_BUCKETS, fetchAgeingBuckets);
}

export function* watchFetchDisbursementByFYGraph() {
  yield takeEvery(DashboardReduxTypes.FETCH_DISBURSEMENT_BY_FY_GRAPH, fetchDisbursementByFYGraph);
}

function* dashboardSaga() {
  yield all([
    fork(watchFetchDashboardOperationsSummary),
    fork(watchFetchRenewalAndExpiry),
    fork(watchFetchTxnsByUser),
    fork(watchFetchProgramPerformance),
    fork(watchFetchProductMix),
    fork(watchFetchDashboardPortfolioAnalyticsSummary),
    fork(watchFetchAgeingBuckets),
    fork(watchFetchDisbursementByFYGraph)
  ]);
}

export default dashboardSaga;
